import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { debounce } from 'lodash';
import ToastService from "../../../../services/ToastService";
import SampleService from "../../../../services/SampleService";
import { FilterListWithInfinityScroll } from "../../../../base/components/FilterListWithInfinityScroll";
import TableSearch from "../../../../base/components/Table/tableSearch";
import EditSampleModal from "./EditSampleModal";
import Table from '../../../../base/components/Table';
import {
  usePaginationProvider,
  useSearchProvider,
  useLocationSource
} from "../../../../base/components/Table/hooks";
import { LIMIT_OPTIONS } from "../../../../base/constants/pagination";
import { canViewService, isServiceEditable } from "../../../../base/helpers/adminRolePermissions";
import { BLOOD_BIOMARKERS_SERVICE_NAME, DNA_BIOMARKERS_SERVICE_NAME, FOOD_INTOLERANCE_SERVICE_NAME } from "../../config";

const AllSamplesTab = () => {
  const isBloodCategoryVisible = canViewService(BLOOD_BIOMARKERS_SERVICE_NAME);
  const isDnaCategoryVisible = canViewService(DNA_BIOMARKERS_SERVICE_NAME);
  const isFoodIntoleranceCategoryVisible = canViewService(FOOD_INTOLERANCE_SERVICE_NAME);

  const enabledCategories = [
    isBloodCategoryVisible && { id: 1, label: "Blood" },
    isDnaCategoryVisible && { id: 2, label: "DNA" },
    isFoodIntoleranceCategoryVisible && { id: 3, label: "Food Intolerance" }
  ].filter(Boolean);

  const [samples, setSamples] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSample, setSelectedSample] = useState(null);
  
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterTestKitTypes, setFilterTestKitTypes] = useState([]);
  const [activatedAtFrom, setActivatedAtFrom] = useState("");
  const [activatedAtTo, setActivatedAtTo] = useState("");
  
  const [tempFilterCategory, setTempFilterCategory] = useState([]);
  const [tempFilterTestKitTypes, setTempFilterTestKitTypes] = useState([]);
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  
  const locationSource = useLocationSource();

  const paginationProvider = usePaginationProvider({
    source: locationSource,
    alias: "offset",
    scope: "",
    fallback: 0
  });

  const limitProvider = usePaginationProvider({
    source: locationSource,
    alias: "limit",
    scope: "",
    fallback: 10,
    onApplyClearScope: ["offset"]
  });

  const searchProvider = useSearchProvider({
    source: locationSource,
    scope: "",
    alias: 'search',
    onApplyClearScope: ["offset"]
  });

  const offset = paginationProvider.getValue() || 0;
  const limit = limitProvider.getValue() || 10;
  const search = searchProvider.getValue() || "";

  const toastService = new ToastService();
  const sampleService = new SampleService();
  const today = new Date();

  const allCategories = [
    { id: 1, label: "Blood" },
    { id: 2, label: "DNA" },
    { id: 3, label: "Food Intolerance" }
  ];
  const categoryOptions = enabledCategories;

  const testKitTypeOptions = [
    { id: 1, label: "Male" },
    { id: 2, label: "Female" }
  ];

  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchSamples(
      search,
      filterCategory,
      filterTestKitTypes,
      activatedAtFrom,
      activatedAtTo,
      Math.floor(offset / limit) + 1,
      limit
    );
  }, [offset, limit, search, filterCategory, filterTestKitTypes, activatedAtFrom, activatedAtTo]);

  useEffect(() => {
    if (isFilterOpen) {
      setTempFilterCategory([...filterCategory]);
      setTempFilterTestKitTypes([...filterTestKitTypes]);
      setTempDateRange([
        activatedAtFrom ? new Date(activatedAtFrom) : null,
        activatedAtTo ? new Date(activatedAtTo) : null
      ]);
    }
  }, [isFilterOpen, filterCategory, filterTestKitTypes, activatedAtFrom, activatedAtTo]);

  const fetchSamples = async (
    searchQuery = "",
    categories = [],
    testKitTypes = [],
    dateFrom = "",
    dateTo = "",
    pageArg = 1,
    pageSizeArg = 20
  ) => {
    setIsLoading(true);
    try {
      const params = {
        limit: pageSizeArg,
        offset: (pageArg - 1) * pageSizeArg,
        orderBy: "createdAt",
        orderType: "desc",
      };

      if (searchQuery) params.query = searchQuery;
      
      const filterFields = [];
      const filterValues = [];
      
      if (categories && categories.length > 0) {
        categories.forEach(categoryId => {
          filterFields.push('category');
          filterValues.push(categoryId);
        });
      }
      
      if (testKitTypes && testKitTypes.length > 0) {
        testKitTypes.forEach(typeId => {
          filterFields.push('testKitTypes');
          filterValues.push(typeId);
        });
      }
      
      if (filterFields.length > 0) {
        params.filterFields = filterFields;
        params.filterValues = filterValues;
      }
      
      if (dateFrom && dateTo) {
        params.activatedAtFrom = dateFrom;
        params.activatedAtTo = dateTo;
      }

      const response = await sampleService.getAllSamples(params);
      setSamples(response.data || []);
      setTotalCount(response.pagination?.totalCount || 0);
    } catch (err) {
      toastService.error("Error fetching samples");
      setSamples([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      searchProvider.setValue(searchTerm);
    }, 500),
    [searchProvider]
  );

  const handleSearchChange = (value) => {
    searchProvider.setValue(value);
    if (value.length >= 3 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  const applyFilters = () => {
    setFilterCategory(tempFilterCategory);
    setFilterTestKitTypes(tempFilterTestKitTypes);
    const dateFrom = tempDateRange[0] ? tempDateRange[0].toISOString().split('T')[0] : "";
    const dateTo = tempDateRange[1] ? tempDateRange[1].toISOString().split('T')[0] : "";
    setActivatedAtFrom(dateFrom);
    setActivatedAtTo(dateTo);
    fetchSamples(search, tempFilterCategory, tempFilterTestKitTypes, dateFrom, dateTo, 1, limit);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setTempFilterCategory([]);
    setTempFilterTestKitTypes([]);
    setTempDateRange([null, null]);
    setFilterCategory([]);
    setFilterTestKitTypes([]);
    setActivatedAtFrom("");
    setActivatedAtTo("");
    fetchSamples(search, [], [], "", "", 1, limit);
  };

  const hasActiveFilters = useMemo(() => {
    return filterCategory.length > 0 || filterTestKitTypes.length > 0 || (activatedAtFrom && activatedAtTo);
  }, [filterCategory, filterTestKitTypes, activatedAtFrom, activatedAtTo]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleEdit = (sample) => {
    setSelectedSample(sample);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (sampleId, updateData) => {
    try {
      const response = await sampleService.updateSample(sampleId, updateData);

      setSamples(prevSamples =>
        prevSamples.map(sample => {
          if (sample.sampleId === sampleId) {
            let updated = { ...sample, ...response };
            if (response.testKitTypes !== undefined) {
              updated.testKitTypes = response.testKitTypes;
            }
            return updated;
          }
          return sample;
        })
      );

      toastService.success("Sample updated successfully");
      setIsEditModalOpen(false);
      setSelectedSample(null);
    } catch (err) {
      toastService.error(err?.response?.data?.message || "Error updating sample");
    }
  };

  const getCategoryLabel = (id) => {
    const found = allCategories.find(opt => opt.id === id || opt.label === id);
    return found ? found.label : id || "";
  };

  const getTestKitTypeLabel = (id) => {
    const found = testKitTypeOptions.find(opt => opt.id === id || opt.label === id);
    return found ? found.label : id || "";
  };

  const columns = [
    {
      Header: "Category",
      accessor: "category",
      Cell: ({ value }) => getCategoryLabel(value),
      width: 120
    },
    {
      Header: "Sample ID",
      accessor: "sampleId",
      width: 140
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      Cell: ({ value }) => formatDate(value),
      width: 120
    },
    {
      Header: "Activated",
      accessor: "isActivated",
      Cell: ({ value }) => value ? "Yes" : "No",
      width: 100
    },
    {
      Header: "Activated At",
      accessor: "activatedAt",
      Cell: ({ value }) => value ? formatDate(value) : "-",
      width: 120
    },
    {
      Header: "Test Kit Type",
      accessor: "testKitTypes",
      Cell: ({ value }) => getTestKitTypeLabel(value),
      width: 120
    },
    {
      Header: "IH Product ID",
      accessor: "IHProductId",
      width: 140
    },
    {
      Header: "Lab Name",
      accessor: "labName",
      width: 150
    },
    {
      Header: "Product Name",
      accessor: "productName",
      width: 200
    },
    {
      Header: "Lab Profile ID",
      accessor: "labProfileId",
      width: 140
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: 100,
      Cell: ({ row }) => (
        <Button
          color="primary"
          size="sm"
          onClick={() => handleEdit(row.original)}
        >
          Edit
        </Button>
      )
    }
  ];

  const getFilteredColumns = () => {
    const isSamplesPageEditable = isServiceEditable("Samples page");

    if (isSamplesPageEditable) {
      return columns;
    }
    return columns.filter(col => col.accessor !== "actions");
  };

  const commonPlaceholder = (
      <div className="text-center">
        No samples found.
      </div>
  );

  const placeholderForSearch = (
      <div className="text-center">
        No matching samples.
      </div>
  ); 

  return (
    <>
      <div className="d-flex align-items-center mb-3 gap-2">
        <TableSearch
          search={search}
          onSearch={val => searchProvider.setValue(val)}
          placeholder="Search by Sample ID, IH Product ID, Lab Profile ID"
          className="notification-search"
          style={{ maxWidth: 400 }}
        />
        
        <Dropdown isOpen={isFilterOpen} toggle={() => setIsFilterOpen(v => !v)}>
          <DropdownToggle className="btn btn-outline-primary d-flex align-items-center" tag="button">
            <i className="bx bx-filter-alt me-2"/> Add filter
          </DropdownToggle>
          <DropdownMenu className="p-2 pt-3" style={{ minWidth: '500px' }}>
            <section className="d-flex justify-content-between gap-3">
              <FilterListWithInfinityScroll
                label="CATEGORY"
                filterState={tempFilterCategory}
                updateFilterState={setTempFilterCategory}
                getData={() => Promise.resolve({ 
                  data: categoryOptions, 
                  pagination: { totalCount: categoryOptions.length } 
                })}
                mapOptions={(options) => options.map(({ id, label }) => ({ id, label }))}
                showAllOption
              />

              {categoryOptions.some(opt => opt.label === "Blood") && (
                tempFilterCategory.length === 0 ||
                (tempFilterCategory.length > 0 &&
                tempFilterCategory.includes(categoryOptions.find(opt => opt.label === "Blood")?.id))) && (
                  <FilterListWithInfinityScroll
                    label="TEST KIT TYPE"
                    filterState={tempFilterTestKitTypes}
                    updateFilterState={setTempFilterTestKitTypes}
                    getData={() => Promise.resolve({ 
                      data: testKitTypeOptions, 
                      pagination: { totalCount: testKitTypeOptions.length } 
                    })}
                    mapOptions={(options) => options.map(({ id, label }) => ({ id, label }))}
                    showAllOption
                  />
              )}
              
              <div style={{ minWidth: '180px' }}>
                <label className="mb-1 fw-bold">ACTIVATED DATE RANGE</label>
                <DatePicker
                  selectsRange
                  startDate={tempDateRange[0]}
                  endDate={tempDateRange[1]}
                  onChange={(update) => {
                    if (update[0] && update[1]) {
                      const [start, end] = update;
                      const normalizedStart = new Date(start);
                      const normalizedEnd = new Date(end);
                      normalizedStart.setHours(0, 0, 0, 0);
                      normalizedEnd.setHours(23, 59, 59, 999);
                      setTempDateRange([normalizedStart, normalizedEnd]);
                    } else {
                      setTempDateRange(update);
                    }
                  }}
                  maxDate={today}
                  isClearable
                  className="form-control"
                  placeholderText="Select date range"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  dateFormat="dd/MM/yyyy"
                />
                {tempDateRange[0] && tempDateRange[1] && (
                  <div className="mt-2 text-muted small">
                    {formatDateDisplay(tempDateRange[0])} - {formatDateDisplay(tempDateRange[1])}
                  </div>
                )}
              </div>
            </section>
            <section className="w-100 px-2 pb-2 mt-3">
              <Button color="primary" onClick={applyFilters} className="w-100">
                Apply
              </Button>
            </section>
          </DropdownMenu>
        </Dropdown>
        
        {hasActiveFilters && (
          <Button color="link" onClick={clearFilters} className="text-danger">
            Clear filter
          </Button>
        )}
      </div>

      <Table
        columns={getFilteredColumns()}
        data={samples}
        loading={isLoading}
        totalCount={totalCount}
        limit={limit}
        offset={offset}
        paginationProvider={paginationProvider}
        searchProvider={searchProvider}
        isLimitEditable
        limitProvider={limitProvider}
        limitOptions={LIMIT_OPTIONS}
        className="table-centered table-nowrap"
        commonPlaceholder={commonPlaceholder}
        placeholderForSearch={placeholderForSearch}
      />

      <EditSampleModal
        isOpen={isEditModalOpen}
        toggle={() => {
          setIsEditModalOpen(false);
          setSelectedSample(null);
        }}
        sample={selectedSample}
        onSave={handleSaveEdit}
        categoryOptions={categoryOptions}
      />
    </>
  );
};

export default AllSamplesTab;
